import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { theme } from '../theme';
import { useResponsive } from '../hooks/useResponsive';
import { useNotification } from './Notification';
import { useGroups, useGroupInvitations } from '../hooks/useGroups';
import type { GroupListItem } from '../types';
import { NoGroupCard } from './home';

const GroupsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { showNotification } = useNotification();
  const { groups, loading, refetch: refetchGroups } = useGroups({ onError: (msg) => showNotification(msg, 'error') });
  const { invitations, loading: loadingInvitations, accept, reject } = useGroupInvitations({
    onError: (msg) => showNotification(msg, 'error'),
    onAccept: (groupId) => {
      showNotification('Te uniste al grupo', 'success');
      refetchGroups();
      if (groupId) navigate(`/groups/${groupId}`);
    },
    onReject: () => showNotification('Invitación rechazada', 'info'),
  });
  const [actingId, setActingId] = useState<string | null>(null);
  const invitationsRef = useRef<HTMLDivElement>(null);

  const handleViewInvitations = () => {
    if (invitations.length > 0) {
      invitationsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      showNotification('No tenés invitaciones a grupos pendientes', 'info');
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: theme.colors.background,
        padding: isMobile ? '80px 16px 20px' : '20px 40px',
      }}
    >
      <AppHeader title="Grupos" showBackButton />

      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 24,
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? 24 : 28,
            fontWeight: 'bold',
            color: theme.colors.text,
            margin: 0,
            fontFamily: theme.fonts.heading,
          }}
        >
          🎯 Mis grupos
        </h1>
        <div style={{ display: 'flex', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
          <button
            type="button"
            onClick={handleViewInvitations}
            style={{
              position: 'relative',
              padding: isMobile ? '12px 20px' : '10px 20px',
              background: 'transparent',
              color: theme.colors.text,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: theme.fonts.body,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            🎯 Invitaciones
            {invitations.length > 0 && (
              <span
                style={{
                  background: theme.colors.primary,
                  color: theme.colors.background,
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 700,
                  minWidth: 20,
                  height: 20,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0 6px',
                }}
              >
                {invitations.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/groups/create')}
            style={{
              padding: isMobile ? '12px 20px' : '10px 20px',
              background: theme.colors.primary,
              color: theme.colors.background,
              border: 'none',
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: theme.fonts.body,
            }}
          >
            + Crear grupo
          </button>
        </div>
      </div>

      {!loadingInvitations && invitations.length > 0 && (
        <div
          ref={invitationsRef}
          style={{
            background: theme.colors.surface,
            borderRadius: 20,
            padding: 24,
            marginBottom: 24,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: `2px solid ${theme.colors.primary}`,
          }}
        >
          <h3 style={{ margin: '0 0 16px 0', fontSize: 18, color: theme.colors.text, fontFamily: theme.fonts.heading }}>
            🎯 Invitaciones a grupos
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: 14, color: theme.colors.textSecondary }}>
            Te invitaron a unirte a estos grupos. Aceptá o rechazá.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {invitations.map((inv) => (
              <div
                key={inv.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 16px',
                  background: theme.colors.background,
                  borderRadius: 12,
                  border: `1px solid ${theme.colors.border}`,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: inv.inviter?.avatar ? 'transparent' : theme.colors.secondary,
                    overflow: 'hidden',
                    flexShrink: 0,
                  }}
                >
                  {inv.inviter?.avatar ? (
                    <img src={inv.inviter.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 20 }}>👤</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: theme.colors.text }}>{inv.group.name}</div>
                  <div style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                    {inv.inviter?.name ?? 'Alguien'} te invitó
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    disabled={actingId === inv.id}
                    onClick={async () => {
                      setActingId(inv.id);
                      await reject(inv.id);
                      setActingId(null);
                    }}
                    style={{
                      padding: '8px 14px',
                      background: 'transparent',
                      color: theme.colors.textSecondary,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: actingId === inv.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Rechazar
                  </button>
                  <button
                    type="button"
                    disabled={actingId === inv.id}
                    onClick={async () => {
                      setActingId(inv.id);
                      await accept(inv.id);
                      setActingId(null);
                    }}
                    style={{
                      padding: '8px 14px',
                      background: theme.colors.primary,
                      color: theme.colors.background,
                      border: 'none',
                      borderRadius: 10,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: actingId === inv.id ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {actingId === inv.id ? '...' : 'Aceptar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div
        style={{
          background: theme.colors.surface,
          borderRadius: 20,
          padding: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: `1px solid ${theme.colors.border}`,
        }}
      >
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <div
              style={{
                width: 40,
                height: 40,
                border: `3px solid ${theme.colors.border}`,
                borderTopColor: theme.colors.primary,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
          </div>
        ) : groups.length === 0 ? (
          <NoGroupCard variant="empty-state" />
        ) : (
          <div
            style={{
              display: 'grid',
              gap: 12,
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            }}
          >
            {groups.map((g: GroupListItem) => (
              <button
                type="button"
                key={g.id}
                onClick={() => navigate(`/groups/${g.id}`)}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/groups/${g.id}`)}
                style={{
                  padding: 18,
                  background: theme.colors.background,
                  borderRadius: 14,
                  border: `1px solid ${theme.colors.border}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  width: '100%',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.primary;
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.colors.border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 17, color: theme.colors.text, marginBottom: 6 }}>
                  {g.name}
                </div>
                {g.description && (
                  <div
                    style={{
                      fontSize: 13,
                      color: theme.colors.textSecondary,
                      marginBottom: 10,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {g.description}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, fontSize: 13, color: theme.colors.textSecondary }}>
                  <span>👥 {g.memberCount}</span>
                  <span>⚽ {g.matchCount}</span>
                  {g.role === 'ADMIN' && (
                    <span style={{ color: theme.colors.primary, fontWeight: 600 }}>Admin</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default GroupsPage;
